import { LinkedDataTransaction, PaginatedResponse, TransactionRule, TransactionRuleType, ApplyRuleToTransactionsResult } from '@types'

import api, { getPaginatedRequest } from '@/store/api'

import { PAGE_SIZE } from '@/const/common'


export const fetchRules = async <T>(
  type: TransactionRuleType,
  repository_id: string,
  search?: string,
  page_no = 0,
  signal?: AbortSignal,
) => {
  try {
    const result = await getPaginatedRequest<TransactionRule<T[]>>(
      'rules',
      {
        repository_id,
        type,
        search,
        page_no,
      },
      signal,
    )
    return result
  } catch (e) {
    throw Error(e as string)
  }
}

export const fetchRuleTransactions = async <T>(
  type: TransactionRuleType,
  ruleData: TransactionRule<T[]>['data'],
  repository_id: string,
  page_no = 0,
  page_size = PAGE_SIZE,
  signal?: AbortSignal,
) => {
  try {
    const { data } = await api.post<
      PaginatedResponse<LinkedDataTransaction>
    >(
      'rules/transactions_preview',
      {
        ...ruleData,
        type,
        repository_id,
        page_no,
        page_size,
      },
      { signal },
    )
    return data
  } catch (e) {
    throw Error(e as string)
  }
}


export const storeRule = async <T>(
  type: TransactionRuleType,
  ruleData: Partial<TransactionRule<T[]>>,
  repository_id: string,
): Promise<TransactionRule<T[]> | undefined> => {
  try {
    const result = await api.post('rules', {
      ...ruleData,
      type,
      repository_id,
    })
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}

export const updateRule = async <T>(
  id: string,
  data: Partial<TransactionRule<T[]>>,
): Promise<TransactionRule<T[]> | undefined> => {
  try {
    const result = await api.put(`rules/${id}`, {
      ...data,
    })
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}

export const reorderRules = async <T>(
  id: string,
  repository_id: string,
  newIndex: number,
  oldIndex: number,
  list: T[]
): Promise<TransactionRule<T[]> | undefined> => {
  try {
    const result = await api.put(`rules/${id}/reorder`, {
      repository_id,
      order: newIndex + 1,
    })
    const item = list[oldIndex]
    list.splice(oldIndex, 1)
    list.splice(newIndex, 0, item)
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}

export const destroyRule = async (id: string, repository_id: string) => {
  try {
    await api.delete(`rules/${id}?repository_id=${repository_id}`)
  } catch (e) {
    throw Error(e as string)
  }
}

export const applyRule = async <T>(
  ruleData: TransactionRule<T[]>['data'],
  repository_id: string,
): Promise<ApplyRuleToTransactionsResult> => {
  try {
    const result = await api.post<ApplyRuleToTransactionsResult>('rules/apply', {
      ...ruleData,
      repository_id,
    })
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}

export const enableRule = async (id: string, repository_id: string) => {
  try {
    const result = await api.put(`rules/${id}/enable`, {
      repository_id,
    })
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}

export const disableRule = async (id: string, repository_id: string) => {
  try {
    const result = await api.put(`rules/${id}/disable`, {
      repository_id,
    })
    return result.data
  } catch (e) {
    throw Error(e as string)
  }
}
